/**
 * Remote Registry Hook
 *
 * React hook for accessing and subscribing to the remote registry.
 */
import { useEffect, useState } from 'react';

import { remoteRegistry } from '../registry/RemoteRegistry';
import type { RemoteComponentConfig } from '../registry/types';

/**
 * Hook to access all registered remotes
 */
export function useRemoteRegistry() {
  const [remotes, setRemotes] = useState<RemoteComponentConfig[]>(() => remoteRegistry.getAll());

  useEffect(() => {
    const unsubscribe = remoteRegistry.subscribe(() => {
      setRemotes(remoteRegistry.getAll());
    });

    return unsubscribe;
  }, []);

  return remotes;
}

/**
 * Hook to access only enabled remotes
 */
export function useEnabledRemotes() {
  const [remotes, setRemotes] = useState<RemoteComponentConfig[]>(() =>
    remoteRegistry.getEnabled(),
  );

  useEffect(() => {
    const unsubscribe = remoteRegistry.subscribe(() => {
      setRemotes(remoteRegistry.getEnabled());
    });

    return unsubscribe;
  }, []);

  return remotes;
}

/**
 * Hook to get a specific remote by id
 */
export function useRemote(id: string) {
  const [remote, setRemote] = useState<RemoteComponentConfig | undefined>(() =>
    remoteRegistry.get(id),
  );

  useEffect(() => {
    const unsubscribe = remoteRegistry.subscribe(() => {
      setRemote(remoteRegistry.get(id));
    });

    return unsubscribe;
  }, [id]);

  return remote;
}

/**
 * Hook to get all required entity types across all remotes
 */
export function useRequiredEntityTypes() {
  const [entityTypes, setEntityTypes] = useState<string[]>(() =>
    remoteRegistry.getRequiredEntityTypes(),
  );

  useEffect(() => {
    const unsubscribe = remoteRegistry.subscribe(() => {
      setEntityTypes(remoteRegistry.getRequiredEntityTypes());
    });

    return unsubscribe;
  }, []);

  return entityTypes;
}

/**
 * Hook to get registry statistics
 */
export function useRemoteRegistryStats() {
  const [stats, setStats] = useState(() => remoteRegistry.getStats());

  useEffect(() => {
    const unsubscribe = remoteRegistry.subscribe(() => {
      setStats(remoteRegistry.getStats());
    });

    return unsubscribe;
  }, []);

  return stats;
}
